import React, { useState, useEffect, useRef } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, message } from "antd";
import { useNavigate } from "react-router-dom";
import GenericTable from "../../../Components/Tables/GenericTable";
import GenericModal from "../../../Components/Modals/GenericModal";
import GenericModalForm from "../../../Components/Modals/GenericModalForm";
import EmailIcon from "../../../Assets/Icons/envelop.svg";
import SettingsUserIcon from "../../../Assets/Icons/settings_user.svg";
import {
  getStaff,
  createStaff,
  getStaffById,
  updateStaff,
  deleteStaff,
} from "../../../API/FuelVendor/StaffManagement";
import { getRoles } from "../../../API/FuelVendor/MyProfile";
import usePageAccess from "../../../Utlis/usePageAccess";
import { getInitials } from "../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const ROLE_BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const ROLE_BADGE_BASE_STYLE = {
  padding: "5.5px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 500,
  display: "inline-block",
};

const getRoleBadgeStyle = (role) => {
  if (!role || role === "-") {
    return { ...ROLE_BADGE_BASE_STYLE, background: "#f4f4f5", color: "#71717a" };
  }
  return {
    ...ROLE_BADGE_BASE_STYLE,
    ...ROLE_BADGE_COLORS[hashString(role) % ROLE_BADGE_COLORS.length],
  };
};

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const formatRelativeTime = (isoDate) => {
  if (!isoDate) return "Never";
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return "Never";

  const diffMins = Math.floor((Date.now() - then) / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} mins ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
};

const mapStaff = (item) => ({
  key: String(item.id),
  staff_name: item.name,
  logo: resolveAvatarUrl(item.avatar),
  email: item.email,
  role: item.role || "-",
  last_login: formatRelativeTime(item.last_login),
  status: capitalize(item.status),
  auto_logout: !!item.auto_logout,
});

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const MAX_FILENAME_LENGTH = 60;

const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
  if (file.name.length <= maxLength) return file;

  const dotIndex = file.name.lastIndexOf(".");
  const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
  const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
  const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

  return new File([file], `${truncatedBase}${ext}`, { type: file.type });
};

const StaffManagement = () => {
  const { canEdit } = usePageAccess("staff-management");
  const [selectedRow, setSelectedRow] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [dataSource, setDataSource] = useState([]);

  const navigate = useNavigate();

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await getStaff();
        const staff = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(staff)) {
          setDataSource(staff.map(mapStaff));
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load staff."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load staff."),
        );
      }
    };

    fetchStaff();
  }, []);

  const fetchRoles = async () => {
    if (roleOptions.length) return;

    try {
      const res = await getRoles();
      const roles = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.results || [];

      if (Math.floor(res?.status / 100) === 2) {
        setRoleOptions(
          roles.map((role) => ({ label: role.name, value: role.id })),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load roles."),
      );
    }
  };

  useEffect(() => {
    if (isAddUserOpen || isEditUserOpen) fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddUserOpen, isEditUserOpen]);

  const normalizeRole = (str) => str?.trim().toLowerCase();

  const currentRoleId = roleOptions.find(
    (r) => normalizeRole(r.label) === normalizeRole(selectedRow?.role),
  )?.value;

  // Until roles are fetched (lazily, on dropdown focus), or if the staff
  // member's role doesn't match any fetched role (renamed/deleted role),
  // keep it as a fallback option so the current selection still shows
  // instead of the field going blank.
  // mapStaff renders a missing role as "-", which isn't a real role.
  const fallbackRole =
    selectedRow?.role && selectedRow.role !== "-" ? selectedRow.role : undefined;

  const roleFieldOptions =
    roleOptions.length && currentRoleId !== undefined
      ? roleOptions
      : fallbackRole
        ? [...roleOptions, { label: fallbackRole, value: fallbackRole }]
        : roleOptions;

  const currentRoleValue =
    currentRoleId !== undefined ? currentRoleId : fallbackRole;

  useEffect(() => {
    if (!isAddUserOpen && !isEditUserOpen) {
      setAvatar(null);
      setAvatarFile(null);
    }
  }, [isAddUserOpen, isEditUserOpen]);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      setAvatarFile(shortenFileName(file));

      const reader = new FileReader();

      reader.onloadend = () => {
        setAvatar(reader.result);
      };

      reader.readAsDataURL(file);
    }
  };
  const handleEdit = async (record) => {
    setSelectedRow(record);
    setAvatar(record.logo);
    setIsEditUserOpen(true);
    fetchRoles();

    try {
      const res = await getStaffById(record.key);
      const data = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && data) {
        const mapped = mapStaff(data);
        setSelectedRow(mapped);
        setAvatar(mapped.logo);
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load staff details."),
      );
    }
  };

  const handleView = (record) => {
    navigate("/staff-detail", {
      state: { user: record },
    });
  };

  const parseLastLogin = (value) => {
    if (value === "Never") return 0;
    if (value.includes("mins")) return -parseInt(value);
    if (value.includes("hours")) return -parseInt(value) * 60;
    if (value.includes("day")) return -parseInt(value) * 60 * 24;
    return 0;
  };

  const columns = [
    {
      title: "Staff",
      dataIndex: "staff_name",
      key: "staff_name",
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {record.logo ? (
            <img
              src={record.logo}
              alt={text}
              style={{ width: 24, height: 24, borderRadius: "50px" }}
            />
          ) : (
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: "50px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                background: getAvatarColor(text),
                flexShrink: 0,
              }}
            >
              {getInitials(text)}
            </span>
          )}
          {text}
        </span>
      ),
      enableSearch: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text, record) => <span>{text}</span>,
      enableSearch: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      filters: [
        ...new Set(
          dataSource.map((item) => item.role).filter((v) => v && v !== "-"),
        ),
      ].map((role) => ({ text: role, value: role })),
      onFilter: (value, record) => record.role === value,
      render: (role) =>
        role === "-" ? (
          role
        ) : (
          <span style={getRoleBadgeStyle(role)}>{role}</span>
        ),
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      sorter: (a, b) =>
        parseLastLogin(a.last_login) - parseLastLogin(b.last_login),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        ...new Set(dataSource.map((item) => item.status).filter(Boolean)),
      ].map((status) => ({ text: status, value: status })),
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const statusClass =
          status === "Active"
            ? "active"
            : status === "Pending"
              ? "pending"
              : "locked";

        return <span className={`status-badge ${statusClass}`}>{status}</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          canEdit && {
            key: "edit",
            label: "Edit",
            style: { color: "#4F46E5", fontWeight: 500 },
            icon: <EditOutlined />,
            onClick: () => {
              handleEdit(record);
            },
          },
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            style: { color: "#000000", fontWeight: 500 },
            onClick: () => handleView(record),
          },
          canEdit && {
            key: "suspended",
            label: "Delete Staff",
            style: { color: " #DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => {
              setSelectedRow(record);
              setIsConfirmOpen(true);
            },
          },
        ].filter(Boolean);
        return (
          <>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 24,
                  height: 24,
                }}
              >
                <BsThreeDotsVertical
                  style={{ fontSize: 20, color: "#4F46E5" }}
                />
              </span>
            </Dropdown>
          </>
        );
      },
    },
  ];

  const getUserModalFields = (isEdit = false) => [
    {
      name: "fullName",
      label: "Full Name",
      required: true,
      placeholder: "Name",
      type: "input",
      prefixIcon: <UserOutlined />,
      rules: [{ required: true, message: "Full Name is required" }],
    },
    {
      name: "role",
      label: "Select Role",
      required: true,
      type: "select",
      placeholder: "Select Role",
      options: isEdit ? roleFieldOptions : roleOptions,
      onFocus: fetchRoles,
      prefixIcon: <img src={SettingsUserIcon} alt="icon" />,
      rules: [{ required: true, message: "Role is required" }],
    },
    {
      name: "email",
      label: "Email",
      required: true,
      placeholder: "Enter Email",
      type: "input",
      defaultValue: isEdit ? selectedRow?.email : "",
      prefixIcon: <img src={EmailIcon} alt="icon" />,
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter a valid email address" },
      ],
    },
    {
      name: "password",
      label: "Password",
      required: !isEdit,
      type: "password",
      placeholder: isEdit ? "Leave blank to keep current password" : "Enter Password",
      prefixIcon: <LockOutlined />,
      rules: isEdit
        ? [
            {
              validator: (_, value) => {
                if (!value || value.length >= 6) return Promise.resolve();
                return Promise.reject(
                  new Error("Password must be at least 6 characters"),
                );
              },
            },
          ]
        : [{ required: true, message: "Please enter password" }],
    },
    {
      name: "confirmPassword",
      label: "Confirm Password",
      required: !isEdit,
      type: "password",
      placeholder: "ReType Password",
      prefixIcon: <LockOutlined />,
      dependencies: ["password"],
      rules: [
        ...(isEdit ? [] : [{ required: true, message: "Please confirm password" }]),
        ({ getFieldValue }) => ({
          validator(_, value) {
            const password = getFieldValue("password");
            if (isEdit && !password) {
              return Promise.resolve();
            }
            if (!value || password === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error("Passwords do not match"));
          },
        }),
      ],
    },
    {
      name: "autoLogout",
      label: "Auto-logout after every transaction",
      type: "switch",
      description:
        "Automatically sign this staff member out once each transaction is completed",
      defaultValue: isEdit ? !!selectedRow?.auto_logout : false,
    },
  ];

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteStaff(selectedRow.key);

      if (Math.floor(res?.status / 100) === 2) {
        setDataSource((prev) =>
          prev.filter((item) => item.key !== selectedRow.key),
        );
        message.success(
          getErrorMessage(res?.data, "Staff deleted successfully"),
        );
        setIsConfirmOpen(false);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete staff."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete staff."),
      );
    }
  };

  const handleAddUser = async (formValues) => {
    const formData = new FormData();
    formData.append("name", formValues.fullName);
    formData.append("email", formValues.email);
    formData.append("role", formValues.role);
    formData.append("password", formValues.password);
    formData.append("auto_logout", !!formValues.autoLogout);
    if (avatarFile) {
      formData.append("avatar", avatarFile);
    }

    try {
      const res = await createStaff(formData);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data;
        if (data) {
          setDataSource((prev) => [mapStaff(data), ...prev]);
        }
        message.success(
          getErrorMessage(res?.data, "Staff created successfully"),
        );
        setIsAddUserOpen(false);
        setAvatar(null);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to create staff."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to create staff."),
      );
    }
  };

  const handleEditUser = async (formValues) => {
    const roleValue = formValues.role ?? currentRoleId;

    const payload = {
      name: formValues.fullName || selectedRow.staff_name,
      email: formValues.email || selectedRow.email,
      auto_logout: !!formValues.autoLogout,
      ...(typeof roleValue === "number" ? { role: roleValue } : {}),
      ...(formValues.password ? { password: formValues.password } : {}),
    };

    try {
      const res = await updateStaff(selectedRow.key, payload);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data || {};
        const updatedRole =
          data.role ||
          roleOptions.find((r) => r.value === payload.role)?.label ||
          selectedRow.role;

        setDataSource((prev) =>
          prev.map((item) =>
            item.key === selectedRow.key
              ? {
                  ...item,
                  staff_name: data.name || payload.name,
                  email: data.email || payload.email,
                  role: updatedRole,
                  auto_logout: payload.auto_logout,
                  logo: resolveAvatarUrl(data.avatar) || avatar || item.logo,
                }
              : item,
          ),
        );
        message.success(
          getErrorMessage(res?.data, "Staff updated successfully"),
        );
        setIsEditUserOpen(false);
        setAvatar(null);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update staff."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update staff."),
      );
    }
  };

  return (
    <div className="user-management-table">
      <GenericTable
        title="Staff"
        dataSource={dataSource}
        columns={columns}
        onRowClick={(r) => console.log(r)}
        enableRowSelection={false}
        onSelectionChange={(keys, rows) => console.log(keys, rows)}
        headerButtonText={canEdit ? "Add Staff" : undefined}
        headerButtonIcon={<PlusOutlined />}
        onHeaderButtonClick={() => setIsAddUserOpen(true)}
      />
      <GenericModal
        open={isConfirmOpen}
        title="Confirm Staff Delete"
        subtitle={`Are you sure you want to delete ${selectedRow?.staff_name}? `}
        highlightText={selectedRow?.staff_name}
        showReason={false}
        confirmText="Confirm Delete"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
      <GenericModalForm
        open={isAddUserOpen}
        title="Add New User"
        subtitle="Create a new account with specific role access"
        okText="Create & Edit Permissions"
        fields={getUserModalFields(false)}
        cancelText="Cancel"
        onCancel={() => setIsAddUserOpen(false)}
        onOk={handleAddUser}
        showProfileUpload={true}
        avatar={avatar}
        onUploadClick={handleButtonClick}
        onFileChange={handleFileChange}
        fileInputRef={fileInputRef}
        onHeaderButtonClick={() => {
          setAvatar(null);
          setIsAddUserOpen(true);
        }}
      />
      <GenericModalForm
        open={isEditUserOpen}
        title="Edit User"
        okText="Save Changes"
        fields={getUserModalFields(true)}
        initialValues={{
          fullName: selectedRow?.staff_name,
          email: selectedRow?.email,
          role: currentRoleValue,
          autoLogout: !!selectedRow?.auto_logout,
        }}
        onCancel={() => setIsEditUserOpen(false)}
        onOk={handleEditUser}
        showProfileUpload={true}
        avatar={avatar}
        onUploadClick={handleButtonClick}
        onFileChange={handleFileChange}
        fileInputRef={fileInputRef}
        onHeaderButtonClick={() => {
          setAvatar(null);
          setIsEditUserOpen(true);
        }}
      />
    </div>
  );
};

export default StaffManagement;
